import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Link,
  useNavigate,
  useParams,
  type LoaderFunctionArgs,
} from "react-router-dom";
import ShapeEdit from "../../../components/Atomos/Icons/ShapeEdit";
import Log from "../../../components/Atomos/Icons/Log";
import { TrashCan } from "../../../components/Atomos/Icons/TrashCan";
import Undo from "../../../components/Atomos/Icons/Undo";
import Modal from "../../../components/Atomos/Modals/Modal";
import { deletePerson, restorePerson } from "../../../Services/Person";
import { toastPromise } from "../../../utils/toast";
import ProfessionalUserLogs from "./ProfessionalUserLogs";
import { IOfficeNew } from "../../../interfaces/models/IOffice";
import { useSelector } from "react-redux";
import { Appstore } from "../../../redux";
import type { IPersonView } from "../../../interfaces/models/IPersonView";
import DataAdminProfile from "./DataAdminProfile";
import type { DataAdminView } from "../../../interfaces/models/IDataAdmin";
import { Title } from "../../../Enums/PersonEnums";
import FilteredTableCore from "../../../components/Organismos/Table/FilteredTableCore";
import PersistentLink from "../../../components/Moleculas/PersistentLink";
import Button from "../../../components/staff/Button";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import Stats from "../../../components/Atomos/Icons/Stats";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import {
  IQuickFilter,
  IFilterData,
  type TableBannerRenderContext,
} from "../../../components/Organismos/Table/FilteredTable.types";
import FirmContactProfile from "./FirmContactProfile";
import MainContactProfile from "./MainContactProfile";
import ProfessionalsFilterStats from "./ProfessionalsFilterStats";
import { FirmContactView } from "../../../interfaces";
import type { IOfficeMainContact } from "../../../interfaces/models/IOfficeMainContact";
import {
  formatOfficeTabLabel,
  getPrimaryOfficeId,
  sortOfficesForProfileBlocks,
} from "./manageDataAdminOfficeRules";
import { formatProfessionalsOfficeFilterLabel } from "./professionalsOfficeFilterLabel";
import { fetchProfessionalsCatalogs } from "./professionalsCatalog";
import { useProfessionalsCatalogData } from "./useProfessionalsCatalogData";
import { useProfessionalsRoutePaths } from "./useProfessionalsRoutePaths";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import { resolvePersonDisplayTitle, formatPersonCatalogDisplayName } from "../../../utils/personDisplayTitle";
import { toReadableLabel } from "../../../utils/contactOptionLabel";
import { getJurisdictionLabel } from "../../../utils/jurisdiction/structuredJurisdiction";
import { isProfessionalsMainContactEnabled } from "../../../Enums/LegacyRedirection";
import { MAX_TABLE_PAGE_SIZE } from "../../../constants/tablePagination";
import {
  formatWsgStaffRoleLabel,
  formatWsgStaffStatusLabel,
  isWsgStaffProfilesFirm,
  STAFF_PROFILES_LABEL,
} from "../wsgStaffProfiles";

export type ProfessionalsLoaderData = {
  offices: IOfficeNew[];
  dataAdmin: DataAdminView[];
  firmContact: FirmContactView[];
  officeMainContacts: IOfficeMainContact[];
};

export async function professionalsLoader({
  params,
}: LoaderFunctionArgs): Promise<ProfessionalsLoaderData> {
  const id = params.id;
  if (!id) {
    throw new Response("Missing firm id", { status: 400 });
  }
  return fetchProfessionalsCatalogs(id);
}

type ProfessionalsProps = {
  dataAdminReadOnly?: boolean;
};

const Professionals = ({ dataAdminReadOnly = false }: ProfessionalsProps) => {
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);
  const { userPreferences, patchUserPreferences } = useUserPreferences();
  const persistedShowDataAdmin =
    userPreferences?.preferences?.professionalsShowDataAdmin ?? false;
  const [optimisticShowDataAdmin, setOptimisticShowDataAdmin] = useState<
    boolean | null
  >(null);
  const { id: firmIdParam } = useParams<{ id: string }>();
  const firmId = firmIdParam ?? manageFirm.selectedFirm?.id;
  const isWsgStaffProfiles = isWsgStaffProfilesFirm(firmId);
  const showDataAdmin =
    optimisticShowDataAdmin !== null
      ? optimisticShowDataAdmin
      : persistedShowDataAdmin;
  const filterFirmId = firmId;
  const routePaths = useProfessionalsRoutePaths(firmId);
  const { catalogData, loading } = useProfessionalsCatalogData(firmId);
  const { isStaffMode } = useStaffMode();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const showMainContactProfile = isProfessionalsMainContactEnabled();

  useEffect(() => {
    if (!isWsgStaffProfiles || !firmId) {
      return;
    }
    const profilesPath = `/managefirm/professionals/${firmId}`.toLowerCase();
    setPageMeta({
      title: STAFF_PROFILES_LABEL,
      breadcrumbLabels: {
        "/managefirm": "Manage Firm",
        [profilesPath]: STAFF_PROFILES_LABEL,
      },
    });
    return () => clearPageMeta();
  }, [clearPageMeta, firmId, isWsgStaffProfiles, setPageMeta]);

  const persistentFilter = useMemo(
    () => ({
      firm: {
        value: filterFirmId,
        comparisonType: "EQUAL" as const,
      },
    }),
    [filterFirmId],
  );

  useEffect(() => {
    if (optimisticShowDataAdmin === null) {
      return;
    }
    if (optimisticShowDataAdmin === persistedShowDataAdmin) {
      setOptimisticShowDataAdmin(null);
    }
  }, [persistedShowDataAdmin, optimisticShowDataAdmin]);

  const officeData: IOfficeNew[] = useMemo(
    () => sortOfficesForProfileBlocks(catalogData.offices),
    [catalogData.offices],
  );
  const [dataAdminData, setDataAdminData] = useState<DataAdminView[]>(
    () => catalogData.dataAdmin,
  );
  const [firmContactData, setFirmContactData] = useState<FirmContactView[]>(
    () => catalogData.firmContact,
  );
  const primaryOfficeContact = useMemo(() => {
    const primaryOfficeId = getPrimaryOfficeId(officeData);
    if (!primaryOfficeId) {
      return undefined;
    }
    return firmContactData.find(
      (contact) => String(contact.officeId ?? "").trim() === primaryOfficeId,
    )?.primaryContact;
  }, [firmContactData, officeData]);
  const [officeMainContactData, setOfficeMainContactData] = useState<
    IOfficeMainContact[]
  >(() => catalogData.officeMainContacts);

  useEffect(() => {
    setDataAdminData(catalogData.dataAdmin);
    setFirmContactData(catalogData.firmContact);
    setOfficeMainContactData(catalogData.officeMainContacts);
  }, [
    firmIdParam,
    firmId,
    catalogData.dataAdmin,
    catalogData.firmContact,
    catalogData.officeMainContacts,
  ]);

  const navigate = useNavigate();
  const [auditLogTarget, setAuditLogTarget] = useState<{
    userId: string;
    name: string;
  } | null>(null);
  const [offboardTarget, setOffboardTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [offboarding, setOffboarding] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [restoring, setRestoring] = useState(false);

  const openAuditLogModal = useCallback(
    (userId: string, name: string) => {
      if (!userId) {
        return;
      }
      setAuditLogTarget({ userId, name });
    },
    [],
  );

  const endpoint = "/person";

  const columnsDef = useMemo(
    () => [
      {
        id: "manage",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => (
          <div className="flex gap-2 text-gray-font">
            <PersistentLink
              className="flex items-center"
              to={routePaths.editProfessional(row.generatedId ?? row.id)}
              state={{ person: (row._apiPerson ?? row) as IPersonView }}
            >
              <div className="w-4 h-4">
                <ShapeEdit />
              </div>
            </PersistentLink>
            {row.userId ? (
              <button
                type="button"
                className="flex items-center text-gray-font"
                aria-label={`View audit log for ${row.name ?? "professional"}`}
                onClick={() =>
                  openAuditLogModal(String(row.userId), String(row.name ?? ""))
                }
              >
                <div className="w-4 h-4">
                  <Log />
                </div>
              </button>
            ) : null}
            {isWsgStaffProfiles && !dataAdminReadOnly && row.isDeleted ? (
              <button
                type="button"
                className="flex items-center text-gray-font"
                aria-label={`Restore ${row.name ?? "staff member"}`}
                onClick={() => {
                  const person = (row._apiPerson ?? row) as IPersonView;
                  const id = String(person.id ?? "").trim();
                  if (!id) {
                    return;
                  }
                  setRestoreTarget({
                    id,
                    name: String(row.name ?? "this staff member"),
                  });
                }}
              >
                <div className="w-4 h-4">
                  <Undo />
                </div>
              </button>
            ) : null}
            {isWsgStaffProfiles && !dataAdminReadOnly && !row.isDeleted ? (
              <button
                type="button"
                className="flex items-center text-gray-font"
                aria-label={`Disable ${row.name ?? "staff member"}`}
                onClick={() => {
                  const person = (row._apiPerson ?? row) as IPersonView;
                  const id = String(person.id ?? "").trim();
                  if (!id) {
                    return;
                  }
                  setOffboardTarget({
                    id,
                    name: String(row.name ?? "this staff member"),
                  });
                }}
              >
                <div className="w-4 h-4">
                  <TrashCan />
                </div>
              </button>
            ) : null}
          </div>
        ),
      },
      {
        id: "name",
        header: "Name",
        width: 150,
        cell: ({ row, value }: { rowIndex: number; row: any; value: any }) => {
          const person = row._apiPerson ?? row;
          const profileId =
            person.legacyId ?? person.generatedId ?? person.id;
          return (
          <Link
            to={routePaths.profileAttorney(profileId, person)}
            state={{ person: person as IPersonView }}
            className={`${isStaffMode ? "text-secondary" : "text-primary"}`}
          >
            {value}
          </Link>
          );
        },
      },
      { id: "title", header: "Title / Unlisted Title" },
      ...(isWsgStaffProfiles
        ? [
            { id: "staffRole", header: "Role" },
            { id: "status", header: "Status" },
          ]
        : [
            { id: "mainContact", header: "Main Contact" },
            { id: "firmEmail", header: "Email", width: 200 },
            { id: "mainOffice", header: "Main Office" },
            { id: "isSearchable", header: "LT" },
            { id: "multipleOffices", header: "MO" },
          ]),
    ],
    [
      isWsgStaffProfiles,
      dataAdminReadOnly,
      routePaths,
      isStaffMode,
      openAuditLogModal,
    ],
  );

  const formatData = (data: any[]) => {
    return data.map((d: any) => {
      const _apiPerson = { ...d };
      const multipleOfficesBool = d.additionalOffices?.length > 0;
      const storedMainOfficeName = toReadableLabel(d.mainOfficeName);
      const office = d.mainOffice
        ? officeData.find((o) => o.id === d.mainOffice)
        : undefined;
      const resolvedOfficeLabel = office ? formatOfficeTabLabel(office) : "";
      const additionalOfficeNames = (d.additionalOffices ?? [])
        .map((officeId: string) => officeData.find((o) => o.id === officeId))
        .filter(Boolean)
        .map((o: IOfficeNew) => formatOfficeTabLabel(o));
      return {
        ...d,
        _apiPerson,
        title: resolvePersonDisplayTitle(d, ""),
        isSearchable: d.isSearchable ? "Yes" : "No",
        mainContact: d.mainContact ? "Yes" : "No",
        multipleOffices: multipleOfficesBool
          ? "Yes"
          : !multipleOfficesBool
            ? "No"
            : "",
        name: formatPersonCatalogDisplayName(d),
        staffRole: formatWsgStaffRoleLabel(d.role),
        status: formatWsgStaffStatusLabel(d),
        mainOffice:
          storedMainOfficeName ||
          (resolvedOfficeLabel === "Office" ? "" : resolvedOfficeLabel),
        additionalOfficeNames,
      };
    });
  };

  const [booleanData] = useState<any[]>([
    { id: "yes", name: "Yes" },
    { id: "no", name: "No" },
  ]);

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Office",
        fieldName: "offices",
        type: "radio",
        customTitle: "Offices",
        objectData: (officeData || []).map((o) => ({
          id: o.id ?? getJurisdictionLabel(o),
          name: formatProfessionalsOfficeFilterLabel(o),
        })),
      },
      {
        name: "Title",
        comparison: "BY_NAME",
        disabledWrite: true,
        objectData: Object.entries(Title).map(([key, value]) => ({
          id: key,
          name: String(value),
        })),
      },
      {
        name: "Listing Type",
        fieldName: "isSearchable",
        objectData: booleanData || [],
        comparison: "BOOLEAN",
        disabledWrite: true,
      },
      {
        name: "Multiple Offices",
        fieldName: "multipleOffices",
        objectData: booleanData || [],
        comparison: "BOOLEAN",
        disabledWrite: true,
      },
      {
        name: "Main Contact",
        fieldName: "mainContact",
        objectData: booleanData || [],
        comparison: "BOOLEAN",
        disabledWrite: true,
      },
    ],
    [booleanData, officeData],
  );

  const handleShowDataAdminToggle = () => {
    const next = !showDataAdmin;
    setOptimisticShowDataAdmin(next);
    void patchUserPreferences({
      preferences: {
        ...userPreferences?.preferences,
        professionalsShowDataAdmin: next,
      },
    }).catch(() => {
      setOptimisticShowDataAdmin(null);
    });
  };

  const renderBannerContent = useCallback(
    ({ quickFilterTotals }: TableBannerRenderContext) => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
          <div className="min-w-0 w-full flex-1 order-2">
            <DataAdminProfile
              dataAdminData={dataAdminData}
              offices={officeData}
              membershipType={manageFirm.selectedFirm?.membershipType}
              primaryContact={primaryOfficeContact}
              readOnly={dataAdminReadOnly}
              profileAttorneyPath={routePaths.profileAttorney}
              onDataAdminsChange={
                dataAdminReadOnly ? undefined : setDataAdminData
              }
            />
          </div>
          <ProfessionalsFilterStats
            totals={quickFilterTotals}
            className="w-full shrink-0 lg:w-44 lg:max-w-[11rem] lg:pt-10 order-1"
          />
        </div>
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
            showDataAdmin ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div
            className={`min-h-0 overflow-hidden flex flex-col gap-4 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
              showDataAdmin
                ? "opacity-100"
                : "pointer-events-none select-none opacity-0"
            }`}
          >
            {showMainContactProfile && firmId ? (
              <MainContactProfile
                officeMainContactData={officeMainContactData}
                offices={officeData}
                firmId={firmId}
                onOfficeMainContactChange={setOfficeMainContactData}
              />
            ) : null}
            <FirmContactProfile
              firmContactData={firmContactData}
              offices={officeData}
              membershipType={manageFirm.selectedFirm?.membershipType}
              includeOfficesHiddenFromDirectory={isWsgStaffProfiles}
              onFirmContactChange={setFirmContactData}
            />
          </div>
        </div>
      </div>
    ),
    [
      showDataAdmin,
      dataAdminData,
      officeData,
      primaryOfficeContact,
      manageFirm.selectedFirm?.membershipType,
      firmContactData,
      officeMainContactData,
      firmId,
      dataAdminReadOnly,
      routePaths.profileAttorney,
      showMainContactProfile,
      isWsgStaffProfiles,
    ],
  );

  const quickFilters: IQuickFilter[] = useMemo(() => {
    const chips: IQuickFilter[] = [
      { key: "totalProfiles", label: "Total Profiles" },
      {
        key: "searchable",
        label: "Searchable",
        panelValues: { "Listing Type": [{ id: "yes", name: "Yes" }] },
      },
      {
        key: "nonSearchable",
        label: "Non Searchable",
        panelValues: { "Listing Type": [{ id: "no", name: "No" }] },
      },
    ];
    if (isWsgStaffProfiles) {
      chips.push({
        key: "disabled",
        label: "Disabled",
        hiddenQuery: {
          personVisibility: { value: "disabled", comparisonType: "EQUAL" },
        },
      });
    }
    return chips;
  }, [isWsgStaffProfiles]);

  const professionalsGroupBy = useMemo(
    () => ({
      field: "mainOffice",
      additionalFields: ["additionalOfficeNames"],
      filterName: "Office",
    }),
    [],
  );

  if (loading) {
    return null;
  }

  return (
    <>
      <Modal
        isModalOpen={auditLogTarget != null}
        toggleModal={() => setAuditLogTarget(null)}
        title={
          auditLogTarget?.name
            ? `Audit log — ${auditLogTarget.name}`
            : "Audit log"
        }
        className="max-w-[1200px]"
        bodyClassName="min-h-0 !pt-0 !px-0"
      >
        {auditLogTarget && firmId ? (
          <ProfessionalUserLogs
            key={auditLogTarget.userId}
            userId={auditLogTarget.userId}
            firmId={firmId}
            embeddedInScrollContainer
          />
        ) : null}
      </Modal>
      <Modal
        isModalOpen={offboardTarget != null}
        toggleModal={() => {
          if (!offboarding) {
            setOffboardTarget(null);
          }
        }}
        title="Disable Staff"
        compact
        className="max-w-md"
      >
        <p className="text-gray-800">
          Disable {offboardTarget?.name}? Their account will be disabled and
          they will be removed from active directories, groups, and staff
          lists.
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button
            text="Cancel"
            onClick={() => setOffboardTarget(null)}
            color="neutral"
            variant="outlined"
            size="fixed"
            disabled={offboarding}
          />
          <Button
            text="Disable"
            onClick={() => {
              const target = offboardTarget;
              if (!target) {
                return;
              }
              setOffboarding(true);
              void toastPromise(deletePerson(target.id), {
                pending: "Disabling staff...",
                success: "Staff member disabled.",
              })
                .then(() => {
                  setOffboardTarget(null);
                  setListRefreshKey((n) => n + 1);
                })
                .catch(() => {
                  /* toastPromise already shows the error */
                })
                .finally(() => {
                  setOffboarding(false);
                });
            }}
            color={isStaffMode ? "secondary" : "primary"}
            variant="filled"
            size="fixed"
            disabled={offboarding}
          />
        </div>
      </Modal>
      <Modal
        isModalOpen={restoreTarget != null}
        toggleModal={() => {
          if (!restoring) {
            setRestoreTarget(null);
          }
        }}
        title="Restore Staff"
        compact
        className="max-w-md"
      >
        <p className="text-gray-800">
          Restore {restoreTarget?.name}? They will return to the active Staff
          list and can sign in again.
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button
            text="Cancel"
            onClick={() => setRestoreTarget(null)}
            color="neutral"
            variant="outlined"
            size="fixed"
            disabled={restoring}
          />
          <Button
            text="Restore"
            onClick={() => {
              const target = restoreTarget;
              if (!target) {
                return;
              }
              setRestoring(true);
              void toastPromise(restorePerson(target.id), {
                pending: "Restoring staff...",
                success: "Staff member restored.",
              })
                .then(() => {
                  setRestoreTarget(null);
                  setListRefreshKey((n) => n + 1);
                })
                .catch(() => {
                  /* toastPromise already shows the error */
                })
                .finally(() => {
                  setRestoring(false);
                });
            }}
            color={isStaffMode ? "secondary" : "primary"}
            variant="filled"
            size="fixed"
            disabled={restoring}
          />
        </div>
      </Modal>
      <FilteredTableCore
        key={listRefreshKey}
        columnsDef={columnsDef}
        showRowNumber
        endpoint={endpoint}
        defaultPageSize={MAX_TABLE_PAGE_SIZE}
        persistPageSize={false}
        quickFilterBarAccessory={
          <Button
            text={`${showDataAdmin ? "Hide Contacts" : "Show Contacts"}`}
            color={isStaffMode ? "white" : "primary"}
            variant="outlined"
            size="fixed"
            onClick={() => handleShowDataAdminToggle()}
            icon={<Stats />}
          />
        }
        searchToolbarAccessory={
          <Button
            text="Add Professional"
            color={isStaffMode ? "secondary" : "primary"}
            variant="filled"
            size="fixed"
            onClick={() => navigate(routePaths.createRelative)}
            icon={<Plus />}
          />
        }
        filtersData={filtersData}
        quickFilters={quickFilters}
        groupBy={professionalsGroupBy}
        persistentFilter={persistentFilter as unknown as any[]}
        formatData={formatData}
        excelFilename={`${manageFirm.selectedFirm?.name} ${
          isWsgStaffProfiles ? "Staff Profiles" : "Professionals"
        }.xlsx`}
        showExcelExport
        showSearchInput
        searchPlaceholder="Search by Name, Last Name, Title, Email, Main Office or AOP Ranking"
        bannerContent={renderBannerContent}
        mergePanelFiltersIntoPersistentFilter
        quickFilterRequireSelection
      />
    </>
  );
};

export default Professionals;
